import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { OnboardingCatalogTask, CatalogTaskKind, PreflightKey } from '@hcc/types';

const KEY = ['onboarding-task-catalog'] as const;

export function useOnboardingCatalog() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () =>
      (await api<{ tasks: OnboardingCatalogTask[] }>('/api/admin/onboarding-tasks')).tasks,
  });
}

export interface CatalogTaskInput {
  key: string;
  title: string;
  description?: string;
  kind: CatalogTaskKind;
  approval_required?: boolean;
  preflight_key?: PreflightKey;
}

export function useCreateCatalogTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CatalogTaskInput) =>
      api<{ task: OnboardingCatalogTask }>('/api/admin/onboarding-tasks', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateCatalogTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<CatalogTaskInput>) =>
      api<{ task: OnboardingCatalogTask }>(`/api/admin/onboarding-tasks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useArchiveCatalogTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/admin/onboarding-tasks/${id}/archive`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export interface AssignableCatalogTask {
  id: string;
  title: string;
  kind: string;
  assigned: boolean;
  status: 'pending' | 'awaiting_review' | 'changes_requested' | 'complete' | null;
  published: boolean;
}

export function usePersonCatalogTasks(personId: string) {
  return useQuery({
    queryKey: ['person-catalog-tasks', personId],
    queryFn: async () =>
      (
        await api<{ tasks: AssignableCatalogTask[] }>(
          `/api/admin/onboarding/people/${personId}/tasks`,
        )
      ).tasks,
  });
}

export function useSetPersonCatalogTask(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, assigned }: { taskId: string; assigned: boolean }) =>
      api(`/api/admin/onboarding/people/${personId}/tasks/${taskId}`, {
        method: assigned ? 'POST' : 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['person-catalog-tasks', personId] });
      qc.invalidateQueries({ queryKey: ['person-onboarding', personId] });
    },
  });
}

export function useReopenPersonCatalogTask(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) =>
      api(`/api/admin/onboarding/people/${personId}/tasks/${taskId}/reopen`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['person-catalog-tasks', personId] });
      qc.invalidateQueries({ queryKey: ['person-onboarding', personId] });
    },
  });
}
